import { getPerspectiveId } from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  commitMutation,
  fetchQuery,
  graphql,
  usePreloadedQuery,
} from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { similarityPage_Query } from "@app/queries/similarityPage_Query.graphql";
import type { similarityPage_MergePreviewQuery } from "@app/queries/similarityPage_MergePreviewQuery.graphql";
import type { similarityPage_MergeRulesMutation } from "@app/queries/similarityPage_MergeRulesMutation.graphql";
import type { similarityPage_SplitRuleMutation } from "@app/queries/similarityPage_SplitRuleMutation.graphql";

import type { Delta } from "./components/recapture-dialog";
import MergeRuleDialog from "./components/merge-rule-dialog";

export const similarityQuery = graphql`
  query similarityPage_Query {
    ruleClusters {
      id
      ratio
      sameTarget
      ruleA {
        id
        regex
        targetType
        targetClassification
        stakeholder {
          id
          name
        }
        destinationBankAccount {
          id
          nickname
          name
        }
      }
      ruleB {
        id
        regex
        targetType
        targetClassification
        stakeholder {
          id
          name
        }
        destinationBankAccount {
          id
          nickname
          name
        }
      }
    }
  }
`;

const mergePreviewQuery = graphql`
  query similarityPage_MergePreviewQuery(
    $ruleIds: [ID!]!
    $regex: String
    $matchKind: String
    $perspectiveId: ID
  ) {
    ruleMergePreview(
      ruleIds: $ruleIds
      regex: $regex
      matchKind: $matchKind
      perspectiveId: $perspectiveId
    ) {
      coveringRegex
      coveringMatchKind
      capturedCount
      droppedCount
      manualConflictCount
      truncated
      captured {
        txn {
          id
        }
        postingDate
        description
        amount
        accountNumber
        expenseNature {
          id
          name
        }
      }
      droppedRuleAssigned {
        txn {
          id
        }
        postingDate
        description
        amount
        accountNumber
        expenseNature {
          id
          name
        }
      }
      droppedManual {
        txn {
          id
        }
        postingDate
        description
        amount
        accountNumber
        expenseNature {
          id
          name
        }
      }
    }
  }
`;

const mergeRulesMutation = graphql`
  mutation similarityPage_MergeRulesMutation($input: MergeRulesInputObject!) {
    mergeRules(input: $input) {
      rule {
        id
      }
      errors {
        message
      }
    }
  }
`;

const splitRuleMutation = graphql`
  mutation similarityPage_SplitRuleMutation($input: SplitRuleInputObject!) {
    splitRule(input: $input) {
      rules {
        id
      }
      errors {
        message
      }
    }
  }
`;

type Cluster = similarityPage_Query["response"]["ruleClusters"][number];
type ClusterRule = Cluster["ruleA"];

function ruleTarget(rule: ClusterRule): string {
  if (rule.targetType === "CLASSIFICATION")
    return rule.targetClassification ?? "—";
  if (rule.targetType === "INTERNAL_LINK")
    return rule.destinationBankAccount
      ? rule.destinationBankAccount.nickname ||
          rule.destinationBankAccount.name
      : "—";
  return rule.stakeholder?.name ?? "—";
}

export default function SimilarityPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<similarityPage_Query>;
}) {
  const preloaded = usePreloadedQuery(similarityQuery, queryRef);
  const [data, setData] = useState(preloaded);
  useEffect(() => setData(preloaded), [preloaded]);

  const refresh = () => {
    fetchQuery<similarityPage_Query>(environment, similarityQuery, {})
      .toPromise()
      .then((fresh) => fresh && setData(fresh));
  };

  // ---- Merge dialog ------------------------------------------------------
  const [merge, setMerge] = useState<Cluster | null>(null);
  const [mergePattern, setMergePattern] = useState("");
  const [mergeMatchKind, setMergeMatchKind] = useState("PREFIX");
  const [mergeDelta, setMergeDelta] = useState<Delta | null>(null);
  const [mergePreviewing, setMergePreviewing] = useState(false);
  const [mergeSaving, setMergeSaving] = useState(false);

  const openMerge = (cluster: Cluster) => {
    setMerge(cluster);
    setMergeDelta(null);
    setMergePattern("");
    setMergePreviewing(true);
    fetchQuery<similarityPage_MergePreviewQuery>(environment, mergePreviewQuery, {
      ruleIds: [cluster.ruleA.id, cluster.ruleB.id],
      perspectiveId: getPerspectiveId() ?? undefined,
    })
      .toPromise()
      .then((res) => {
        setMergePreviewing(false);
        const preview = res?.ruleMergePreview;
        if (!preview) return;
        setMergePattern(preview.coveringRegex ?? "");
        setMergeMatchKind(preview.coveringMatchKind ?? "PREFIX");
        setMergeDelta(preview as unknown as Delta);
      });
  };

  const runMergePreview = (regex: string, matchKind: string) => {
    if (!merge) return;
    setMergePreviewing(true);
    fetchQuery<similarityPage_MergePreviewQuery>(environment, mergePreviewQuery, {
      ruleIds: [merge.ruleA.id, merge.ruleB.id],
      regex,
      matchKind,
      perspectiveId: getPerspectiveId() ?? undefined,
    })
      .toPromise()
      .then((res) => {
        setMergePreviewing(false);
        if (res?.ruleMergePreview) setMergeDelta(res.ruleMergePreview as unknown as Delta);
      });
  };

  const closeMerge = () => {
    setMerge(null);
    setMergeDelta(null);
    setMergePattern("");
  };

  const confirmMerge = (
    regex: string,
    matchKind: string,
    overrideTxnIds: string[]
  ) => {
    if (!merge) return;
    setMergeSaving(true);
    commitMutation<similarityPage_MergeRulesMutation>(environment, {
      mutation: mergeRulesMutation,
      variables: {
        input: {
          ruleIds: [merge.ruleA.id, merge.ruleB.id],
          regex,
          matchKind,
          overrideTxnIds,
        },
      },
      onCompleted: (res) => {
        setMergeSaving(false);
        if (res.mergeRules?.errors.length) {
          window.alert(res.mergeRules.errors[0].message);
          return;
        }
        closeMerge();
        refresh();
      },
      onError: (err) => {
        setMergeSaving(false);
        window.alert(err.message);
      },
    });
  };

  // ---- Split dialog ------------------------------------------------------
  const [split, setSplit] = useState<{
    rule: ClusterRule;
    regexA: string;
    regexB: string;
  } | null>(null);
  const [splitting, setSplitting] = useState(false);

  const confirmSplit = () => {
    if (!split) return;
    setSplitting(true);
    commitMutation<similarityPage_SplitRuleMutation>(environment, {
      mutation: splitRuleMutation,
      variables: {
        input: {
          ruleId: split.rule.id,
          regexA: split.regexA,
          regexB: split.regexB,
        },
      },
      onCompleted: (res) => {
        setSplitting(false);
        if (res.splitRule?.errors.length) {
          window.alert(res.splitRule.errors[0].message);
          return;
        }
        setSplit(null);
        refresh();
      },
      onError: (err) => {
        setSplitting(false);
        window.alert(err.message);
      },
    });
  };

  const clusters = data.ruleClusters;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-bold">Similar rules</h1>
        <span className="text-sm text-gray-500">
          {clusters.length} pair{clusters.length === 1 ? "" : "s"}, most similar
          first
        </span>
        <Link to="/rules" className="text-sm text-emerald-700 hover:underline">
          ← back to rules
        </Link>
      </div>

      {clusters.length === 0 ? (
        <p className="text-sm text-gray-400 bg-white border rounded p-6">
          No similar rule pairs found.
        </p>
      ) : (
        <div className="space-y-3">
          {clusters.map((cluster) => (
            <div
              key={cluster.id}
              className="bg-white rounded border p-4 space-y-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  similarity {(cluster.ratio * 100).toFixed(0)}%
                  {cluster.sameTarget ? " · same target" : " · different target"}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={!cluster.sameTarget}
                    onClick={() => openMerge(cluster)}
                    title={
                      cluster.sameTarget
                        ? "Merge into one covering rule"
                        : "Only same-target pairs can be merged"
                    }
                    className="px-3 py-1.5 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-30"
                  >
                    Merge
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[cluster.ruleA, cluster.ruleB].map((rule, i) => (
                  <div key={rule.id} className="rounded border p-2 space-y-1">
                    <code className="block font-mono text-xs bg-gray-50 rounded px-1.5 py-1 break-all">
                      {rule.regex}
                    </code>
                    <p className="text-xs text-gray-500">
                      → {ruleTarget(rule)}
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        setSplit({
                          rule,
                          regexA: rule.regex,
                          regexB: rule.regex,
                        })
                      }
                      className="text-[11px] text-gray-400 hover:text-gray-700"
                    >
                      split rule {i === 0 ? "A" : "B"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {merge && (
        <MergeRuleDialog
          initialPattern={mergePattern}
          initialMatchKind={mergeMatchKind}
          delta={mergeDelta}
          previewing={mergePreviewing}
          saving={mergeSaving}
          onPreview={runMergePreview}
          onMerge={confirmMerge}
          onCancel={closeMerge}
        />
      )}

      {split && (
        <div
          className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center"
          onClick={() => setSplit(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-[520px] p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-sm font-semibold text-gray-800">
              Split rule into two
            </h2>
            <p className="text-xs text-gray-500">
              The original rule keeps the first regex; a second rule is created
              with the same target and the second regex.
            </p>
            <div className="space-y-1">
              <label className="text-xs text-gray-500">Regex A (kept)</label>
              <input
                value={split.regexA}
                onChange={(e) =>
                  setSplit({ ...split, regexA: e.target.value })
                }
                className="w-full rounded border px-2 py-1.5 font-mono text-xs"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-gray-500">Regex B (new rule)</label>
              <input
                value={split.regexB}
                onChange={(e) =>
                  setSplit({ ...split, regexB: e.target.value })
                }
                className="w-full rounded border px-2 py-1.5 font-mono text-xs"
              />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                disabled={splitting || !split.regexA.trim() || !split.regexB.trim()}
                onClick={confirmSplit}
                className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
              >
                {splitting ? "Splitting…" : "Split rule"}
              </button>
              <button
                type="button"
                onClick={() => setSplit(null)}
                className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
